import React, { useState } from 'react';
import { Button, InputNumber, Modal, Popconfirm, Tag, Tooltip, Typography, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import diskAdminService from '../../services/diskAdminService';
import AdminProductManageDrawer from '../../components/billing/AdminProductManageDrawer';
import { ModeTag, RoleTag, SearchTable, StatusTag, UserCell, fmtBytes, fmtNum, fmtTime, toastErr } from './common';

const { Text } = Typography;

const ownerCell = (r) => {
    const a = r.account || r.owner || {};
    const email = a.email || r.account_email || r.owner_email;
    const name = a.name || r.account_name || r.owner_name;
    const id = a.id || r.account_id || r.owner_account_id;
    if (!email && !name && !id) return '-';
    return <span>{id ? `#${id} ` : ''}{name || ''}<br /><Text type="secondary" style={{ fontSize: 12 }}>{email || ''}</Text></span>;
};
const fileCell = (r) => {
    const f = r.file || {};
    const name = f.name || r.file_name || r.name;
    return name ? <span>{(f.is_dir || r.is_dir) ? '📁 ' : ''}{name}{(f.size ?? r.file_size) ? <Text type="secondary" style={{ fontSize: 12 }}> · {fmtBytes(f.size ?? r.file_size)}</Text> : null}</span> : '-';
};

const run = async (fn, okText, done) => {
    try {
        await fn();
        message.success(okText);
        done();
    } catch (e) {
        toastErr(e, '操作失败');
    }
};

export const Accounts = () => {
    const [rk, setRk] = useState(0);
    const [manageId, setManageId] = useState(null);
    const [freeEdit, setFreeEdit] = useState(null); // { r, gb }
    const reload = () => setRk((k) => k + 1);
    const saveFree = () => run(
        () => diskAdminService.setAccountFreeStorage(freeEdit.r.id, freeEdit.gb ?? null),
        '已更新', () => { setFreeEdit(null); reload(); },
    );
    const toggle = (r) => {
        const next = r.status === 'active' ? 'disabled' : 'active';
        Modal.confirm({
            title: `${next === 'disabled' ? '停用' : '启用'}账号 ${r.email || r.name || `#${r.id}`}？`,
            content: next === 'disabled' ? '停用后立即无法登录网盘，直链与分享仍按文件状态。' : undefined,
            okType: next === 'disabled' ? 'danger' : 'primary',
            onOk: () => run(() => diskAdminService.setAccountStatus(r.id, next), '已更新', reload),
        });
    };
    const columns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '账号', key: 'n', render: (_, r) => <span>{r.name || '-'}<br /><Text type="secondary" style={{ fontSize: 12 }}>{r.email || r.user_email || ''}</Text></span> },
        { title: '类型', dataIndex: 'role', width: 110, render: (v) => <RoleTag role={v} /> },
        { title: '组织', key: 'o', render: (_, r) => (r.org_id ? <span>#{r.org_id} {r.org_name || r.org?.name || ''}</span> : '-') },
        { title: '控制台用户', key: 'u', render: (_, r) => <UserCell r={r} /> },
        { title: '已用', key: 'used', width: 110, render: (_, r) => fmtBytes(r.used_bytes ?? r.storage_used) },
        { title: '状态', dataIndex: 'status', width: 90, render: (v) => <StatusTag status={v} /> },
        { title: '最近登录', dataIndex: 'last_login_at', width: 170, render: (v, r) => <Tooltip title={r.last_login_ip}>{fmtTime(v)}</Tooltip> },
        {
            title: '操作', key: 'a', width: 140, fixed: 'right',
            render: (_, r) => r.status !== 'deleted' && (
                <span>
                    {r.subscription_id && <Button size="small" style={{ marginInlineEnd: 4 }} onClick={() => setManageId(r.subscription_id)}>配额</Button>}
                    {!r.subscription_id && !r.org_id && (
                        <Button size="small" style={{ marginInlineEnd: 4 }}
                            onClick={() => setFreeEdit({ r, gb: r.admin_storage_bytes == null ? null : (r.admin_storage_bytes < 0 ? -1 : Math.round(r.admin_storage_bytes / 1024 ** 3)) })}>配额</Button>
                    )}
                    <Button size="small" danger={r.status === 'active'} onClick={() => toggle(r)}>{r.status === 'active' ? '停用' : '启用'}</Button>
                </span>
            ),
        },
    ];
    return (
        <>
            <SearchTable title="云盘账号" placeholder="邮箱 / 姓名 / 用户 ID" fetcher={diskAdminService.listAccounts} columns={columns} reloadKey={rk} scrollX={1250} />
            <Modal open={!!freeEdit} title={`免费账号容量 · ${freeEdit?.r?.email || freeEdit?.r?.name || ''}`} onOk={saveFree} onCancel={() => setFreeEdit(null)} destroyOnClose>
                <Tooltip title="留空 = 平台默认免费容量；-1 = 不限">
                    <InputNumber min={-1} step={1} addonAfter="GB" style={{ width: 200 }} placeholder="默认"
                        value={freeEdit?.gb} onChange={(v) => setFreeEdit((f) => ({ ...f, gb: v }))} />
                </Tooltip>
            </Modal>
            <AdminProductManageDrawer open={!!manageId} subscriptionId={manageId} onClose={() => setManageId(null)} onChanged={reload} />
        </>
    );
};

export const Orgs = () => {
    const [rk, setRk] = useState(0);
    const [manageId, setManageId] = useState(null);
    const reload = () => setRk((k) => k + 1);
    const toggle = (r) => {
        const next = r.status === 'active' ? 'suspended' : 'active';
        Modal.confirm({
            title: `${next === 'suspended' ? '暂停' : '恢复'}组织 ${r.name}？`,
            content: next === 'suspended' ? '暂停后组织全部成员无法登录，组织直链与分享失效。' : undefined,
            okType: next === 'suspended' ? 'danger' : 'primary',
            onOk: () => run(() => diskAdminService.setOrgStatus(r.id, next), '已更新', reload),
        });
    };
    const columns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '名称', dataIndex: 'name' },
        { title: '所有者', key: 'u', render: (_, r) => <UserCell r={{ ...r, user_id: r.owner_user_id ?? r.user_id, user: r.owner || r.user }} /> },
        { title: '模式', dataIndex: 'quota_mode', width: 100, render: (v) => <ModeTag mode={v} /> },
        {
            title: '空间', key: 's', width: 170,
            render: (_, r) => {
                const used = r.used_bytes ?? r.usage?.used;
                const limit = r.pool_bytes ?? r.usage?.limit;
                return `${fmtBytes(used)}${limit ? ` / ${fmtBytes(limit)}` : ''}`;
            },
        },
        {
            title: '成员 / 席位', key: 'm', width: 150,
            render: (_, r) => {
                const tiers = r.seat_tiers && typeof r.seat_tiers === 'object' ? r.seat_tiers : null;
                const members = r.members_count ?? r.member_count ?? r.usage?.seats_used;
                return (
                    <span>
                        {fmtNum(members)}{r.seat_limit ? ` / ${r.seat_limit}` : ''}
                        {tiers && Object.keys(tiers).length > 0 && (
                            <div>{Object.entries(tiers).map(([gb, n]) => <Tag key={gb} style={{ marginInlineEnd: 4 }}>{gb}G×{typeof n === 'object' ? n?.total : n}</Tag>)}</div>
                        )}
                    </span>
                );
            },
        },
        { title: '订阅', dataIndex: 'subscription_id', width: 80, render: (v) => (v ? `#${v}` : '-') },
        { title: '状态', dataIndex: 'status', width: 90, render: (v) => <StatusTag status={v} /> },
        { title: '创建', dataIndex: 'created_at', width: 170, render: fmtTime },
        {
            title: '操作', key: 'a', width: 140, fixed: 'right',
            render: (_, r) => r.status !== 'deleted' && (
                <span>
                    {r.subscription_id && <Button size="small" style={{ marginInlineEnd: 4 }} onClick={() => setManageId(r.subscription_id)}>配额</Button>}
                    <Button size="small" danger={r.status === 'active'} onClick={() => toggle(r)}>{r.status === 'active' ? '暂停' : '恢复'}</Button>
                </span>
            ),
        },
    ];
    return (
        <>
            <SearchTable title="组织" placeholder="组织名 / 用户 ID" fetcher={diskAdminService.listOrgs} columns={columns} reloadKey={rk} scrollX={1350} />
            <AdminProductManageDrawer open={!!manageId} subscriptionId={manageId} onClose={() => setManageId(null)} onChanged={reload} />
        </>
    );
};

export const Shares = () => {
    const [rk, setRk] = useState(0);
    const reload = () => setRk((k) => k + 1);
    const revoked = (r) => r.status === 'revoked' || !!r.revoked_at;
    const columns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        {
            title: '分享', key: 't', render: (_, r) => (
                <span>
                    <a href={r.url || `https://disk.yeap.cloud/s/${r.token}`} target="_blank" rel="noopener noreferrer">{r.token}</a>
                    {r.has_password && <Tooltip title="有提取码"><LockOutlined style={{ marginLeft: 6, color: '#999' }} /></Tooltip>}
                    {r.source === 'mail' && <Tag style={{ marginLeft: 6 }}>邮件附件</Tag>}
                </span>
            ),
        },
        { title: '文件', key: 'f', render: (_, r) => fileCell(r) },
        { title: '分享者', key: 'o', render: (_, r) => ownerCell(r) },
        { title: '浏览 / 下载', key: 'c', width: 110, render: (_, r) => `${fmtNum(r.view_count)} / ${fmtNum(r.download_count)}` },
        { title: '过期', dataIndex: 'expires_at', width: 170, render: (v) => (v ? fmtTime(v) : '永久') },
        { title: '状态', key: 's', width: 90, render: (_, r) => <StatusTag status={revoked(r) ? 'revoked' : (r.status || 'active')} /> },
        { title: '创建', dataIndex: 'created_at', width: 170, render: fmtTime },
        {
            title: '操作', key: 'a', width: 90, fixed: 'right',
            render: (_, r) => !revoked(r) && (
                <Popconfirm title="撤销此分享？" okType="danger" onConfirm={() => run(() => diskAdminService.revokeShare(r.id), '已撤销', reload)}>
                    <Button size="small" danger>撤销</Button>
                </Popconfirm>
            ),
        },
    ];
    return <SearchTable title="分享审核" placeholder="token / 文件名 / 邮箱" fetcher={diskAdminService.listShares} columns={columns} reloadKey={rk} scrollX={1300} />;
};

export const Links = () => {
    const [rk, setRk] = useState(0);
    const reload = () => setRk((k) => k + 1);
    const enabledOf = (r) => r.enabled === true || r.enabled === 1 || r.enabled === '1';
    const columns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        {
            title: '直链', key: 't', render: (_, r) => {
                const url = r.url || `https://disk.yeap.cloud/d/${r.token}`;
                return <a href={url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{r.token}</a>;
            },
        },
        { title: '文件', key: 'f', render: (_, r) => fileCell(r) },
        { title: '所有者', key: 'o', render: (_, r) => ownerCell(r) },
        { title: 'Referer 白名单', dataIndex: 'referer_allow', render: (v) => (v ? <Text style={{ fontSize: 12 }}>{v}</Text> : '-') },
        { title: '请求', dataIndex: 'hits', width: 90, render: fmtNum },
        { title: '流量', dataIndex: 'bytes', width: 110, render: fmtBytes },
        { title: '状态', key: 's', width: 90, render: (_, r) => (enabledOf(r) ? <Tag color="green">启用</Tag> : <Tag>已禁用</Tag>) },
        {
            title: '操作', key: 'a', width: 90, fixed: 'right',
            render: (_, r) => (enabledOf(r)
                ? <Popconfirm title="禁用此直链？" okType="danger" onConfirm={() => run(() => diskAdminService.setLinkEnabled(r.id, false), '已禁用', reload)}><Button size="small" danger>禁用</Button></Popconfirm>
                : <Button size="small" onClick={() => run(() => diskAdminService.setLinkEnabled(r.id, true), '已启用', reload)}>启用</Button>),
        },
    ];
    return <SearchTable title="直链" placeholder="token / 文件名 / 邮箱" fetcher={diskAdminService.listLinks} columns={columns} reloadKey={rk} scrollX={1300} />;
};
